import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import SmsOutlinedIcon from '@mui/icons-material/SmsOutlined';

import Avatar from '@/components/Avatar';
import styled from '@/styles';
import {
  lifecycleAPI,
  MessageTimelineItem,
  WorkerLifecycleOverview,
} from '@/services/lifecycleAPI';
import {
  PastJob,
  WorkerDetail,
  WorkerListItem,
  WorkersV2API,
} from '@/services/workersV2API';

import { formatActiveAgo, formatPhone, i9Mismatch } from '../mappings';
import {
  bgStatusFor,
  daysBetween,
  formatDays,
  formatLongDate,
  formatMonthDay,
  formatMonthDayTime,
  i9StatusFor,
} from '../onboardingMappings';
import CardTooltip from './CardTooltip';
import { NOT_AVAILABLE_REASON } from './OnboardingTable';

export const OUTREACH_TOOLTIP =
  'Messages sent to candidate and how they responded.';
const OUTREACH_PREVIEW = 4;

// BH-72: from 769 to 900px a 400px panel beside the list left the table
// about 215px. Here it floats over the list's right side instead; the list
// keeps the full width under it. At 768px and below there is no panel: a
// ?worker= link opens the profile, as a card tap does (GravyWorkers).
export const PANEL_NARROW_OVERLAY = {
  '@touchable': {
    position: 'fixed',
    top: '65px',
    right: '16px',
    bottom: '16px',
    width: 'calc(100vw - 32px)',
    maxHeight: 'none',
    // Above the list; below AppFrameV2's navbar, sidebar and backdrop.
    zIndex: 20,
    boxShadow: '0px 0px 12px 0px rgba(0, 0, 0, 0.12)',
  },
} as const;

const Aside = styled('aside', {
  flex: '0 0 400px',
  maxWidth: '400px',
  alignSelf: 'flex-start',
  // The page scrolls the window under AppFrameV2's fixed 49px navbar, so the
  // panel sticks below it; at `top: 16px` the close button scrolled under the
  // navbar and could not be clicked.
  position: 'sticky',
  top: '65px',
  maxHeight: 'calc(100vh - 81px)',
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: '#FFFFFF',
  border: '1px solid #DADADA',
  borderRadius: '12px',
  overflow: 'hidden',
  ...PANEL_NARROW_OVERLAY,
});
const Scroll = styled('div', { overflowY: 'auto', padding: '20px 24px' });
const CloseBtn = styled('button', {
  appearance: 'none',
  position: 'absolute',
  top: '12px',
  right: '12px',
  // Above the avatar below it, which is positioned and painted later.
  zIndex: 1,
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
  padding: '4px',
  color: '#3D3D3D',
});
const Name = styled('h2', {
  margin: '12px 0 4px',
  fontSize: '18px',
  fontWeight: 700,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});
const Line = styled('div', {
  fontSize: '13px',
  color: '#3D3D3D',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});
// #6B6B6B: 5.3:1 on white and 5.0:1 on the #FAFAFA summary card; the
// earlier #8A8A8A was 3.3-3.45:1 under the 4.5:1 the ticket asks for, on the
// Outreach sent date and day number among others (review R5B-03).
const Faint = styled('div', {
  fontSize: '12px',
  color: '#6B6B6B',
  marginTop: '2px',
});
const Tiles = styled('div', {
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)',
  gap: '8px',
  margin: '16px 0 8px',
});
const Tile = styled('div', {
  padding: '8px 10px',
  borderRadius: '8px',
  backgroundColor: '#F5F5F5',
  fontSize: '12px',
  color: '#3D3D3D',
  minWidth: 0,
  '& strong': {
    display: 'block',
    fontSize: '13px',
    marginTop: '2px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  // Journey names such as "Get to 3 shifts" are wider than a third of the
  // panel; they wrap to a second line instead of losing the shift count to
  // an ellipsis (design vs implemented L3).
  '& strong[data-wrap="true"]': {
    whiteSpace: 'normal',
    overflowWrap: 'anywhere',
  },
  variants: {
    tone: {
      red: { backgroundColor: '#FDECEC', color: '#A11A1A' },
      green: { backgroundColor: '#E7F4E6', color: '#1F6B14' },
      neutral: {},
    },
  },
});
const SectionHead = styled('button', {
  appearance: 'none',
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '8px',
  padding: '16px 0',
  border: 'none',
  borderTop: '1px solid #EAEAEA',
  background: 'transparent',
  fontFamily: 'inherit',
  fontSize: '15px',
  fontWeight: 600,
  color: '#1C1C1C',
  cursor: 'pointer',
});
const HeadLabel = styled('span', {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
});
const SummaryCard = styled('div', {
  padding: '10px 12px',
  border: '1px solid #DADADA',
  borderRadius: '8px',
  backgroundColor: '#FAFAFA',
  marginBottom: '12px',
  fontSize: '14px',
  fontWeight: 600,
});
const Timeline = styled('ol', {
  listStyle: 'none',
  margin: '0 0 12px',
  padding: 0,
});
const Item = styled('li', {
  position: 'relative',
  padding: '0 0 14px 22px',
  '&::before': {
    content: '""',
    position: 'absolute',
    left: '5px',
    top: '14px',
    bottom: 0,
    width: '1px',
    backgroundColor: '#DADADA',
  },
  '&:last-child::before': { display: 'none' },
});
const MarkerSlot = styled('span', {
  position: 'absolute',
  left: '-1px',
  top: '2px',
  display: 'inline-flex',
  backgroundColor: '#FFFFFF',
  borderRadius: '50%',
});
const AlertDot = styled('span', {
  width: '13px',
  height: '13px',
  borderRadius: '50%',
  backgroundColor: '#C62828',
});
// Per the design: a green check-circle for a done step or shift, a grey
// outlined check-circle for one still open, and a red dot for a rejected
// step or a vendor warning (design vs implemented M6).
const Dot = ({ done, alert }: { done?: boolean; alert?: boolean }) => {
  let icon = (
    <CheckCircleOutlineIcon
      sx={{ fontSize: 13, color: '#BDBDBD' }}
      data-marker="open"
    />
  );
  if (alert) icon = <AlertDot data-marker="alert" />;
  else if (done)
    icon = (
      <CheckCircleIcon
        sx={{ fontSize: 13, color: '#44A735' }}
        data-marker="done"
      />
    );
  return <MarkerSlot aria-hidden="true">{icon}</MarkerSlot>;
};
const ItemTitle = styled('div', { fontSize: '14px' });
const ItemSub = styled('div', {
  fontSize: '12px',
  color: '#5A5A5A',
  variants: { alert: { true: { color: '#A11A1A' } } },
});
const Message = styled('li', {
  padding: '12px 0',
  borderBottom: '1px solid #EAEAEA',
  fontSize: '13px',
  '&:last-child': { borderBottom: 'none' },
});
const MessageHead = styled('div', {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '8px',
});
const Channel = styled('span', {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  fontWeight: 600,
});
const Chip = styled('span', {
  padding: '2px 8px',
  borderRadius: '6px',
  fontSize: '12px',
  fontWeight: 500,
  backgroundColor: '#F0F0F0',
  color: '#3D3D3D',
  variants: {
    tone: {
      green: { backgroundColor: '#E7F4E6', color: '#1F6B14' },
      neutral: {},
    },
  },
});
const Quote = styled('div', { marginTop: '4px', color: '#3D3D3D' });
const ViewAll = styled('button', {
  appearance: 'none',
  border: 'none',
  background: 'transparent',
  padding: 0,
  fontFamily: 'inherit',
  fontSize: '13px',
  fontWeight: 600,
  color: '#2E8722',
  cursor: 'pointer',
});
const Footer = styled('div', {
  display: 'flex',
  gap: '8px',
  padding: '12px 24px',
  borderTop: '1px solid #EAEAEA',
  '& > span': { flex: 1, display: 'flex' },
});
const FooterBtn = styled('button', {
  flex: 1,
  height: '40px',
  borderRadius: '8px',
  fontFamily: 'inherit',
  fontSize: '14px',
  fontWeight: 600,
  border: '1px solid #44A735',
  background: '#FFFFFF',
  color: '#2E8722',
  cursor: 'pointer',
  variants: {
    primary: { true: { background: '#44A735', color: '#FFFFFF' } },
  },
  '&:disabled': { opacity: 0.5, cursor: 'not-allowed' },
});
const Muted = styled('div', { fontSize: '13px', color: '#5A5A5A' });

// in_progress from check-in, completed from checkout until the timesheet is
// approved: the worker is on or past that shift, so it is not "No claimed
// shift yet" (review R5B-02). A shift marked as a no-show (showed: false,
// status left hired) is not one the worker is scheduled for.
const CLAIMED_UPCOMING = new Set([
  'hired',
  'confirmed',
  'in_progress',
  'completed',
]);

const ENDED = new Set(['dismissed', 'dropped', 'expired', 'rejected']);

type ShiftEntry = { at: string; role: string };

// The detail endpoint's past_jobs: an approved timesheet is a completed
// shift; any other claimed assignment (CLAIMED_UPCOMING) is a scheduled one,
// including a shift being worked now or waiting for its timesheet to be
// approved: the worker has claimed it, so it is not "No claimed shift yet"
// (review D2-04).
export const activationShifts = (
  jobs: PastJob[],
): { completed: ShiftEntry[]; upcoming: ShiftEntry[] } => {
  const byStart = (a: ShiftEntry, b: ShiftEntry) =>
    new Date(a.at).getTime() - new Date(b.at).getTime();
  // One assignment covers every shift of its job: a 5-shift job is five
  // completed shifts, not one. approved_shift_starts lists them; older graphy
  // sends only the first timesheet, so fall back to it.
  const completed = jobs
    .flatMap((j) => {
      if (j.approved_shift_starts) {
        return j.approved_shift_starts.map((at) => ({
          at,
          role: j.skill_name,
        }));
      }
      return j.timesheet?.approved_at
        ? [
            {
              at: j.shift_start_at ?? j.timesheet.approved_at,
              role: j.skill_name,
            },
          ]
        : [];
    })
    .sort(byStart);
  const nextStart = (j: PastJob): string | null | undefined =>
    'next_shift_start_at' in j
      ? j.next_shift_start_at
      : !j.timesheet?.approved_at
        ? j.shift_start_at
        : null;
  // With next_shift_start_at, approving an earlier shift moves a multi-shift
  // assignment to `approved` while its later shifts are still to come, so
  // only an assignment that ended (dropped, dismissed, expired, rejected)
  // has nothing scheduled. Older graphy: the status alone, as before.
  // A dismissal or drop can be stamped while the status still reads hired or
  // approved, so the timestamps end it too, or a dismissed worker's old shift
  // showed as "Scheduled" (BH-40).
  const stillClaimed = (j: PastJob): boolean => {
    if (j.dismissed_at || j.dropped_at) return false;
    return 'next_shift_start_at' in j
      ? !ENDED.has(j.status)
      : CLAIMED_UPCOMING.has(j.status);
  };
  const upcoming = jobs
    .filter((j) => stillClaimed(j) && j.showed !== false && nextStart(j))
    .map((j) => ({ at: nextStart(j)!, role: j.skill_name }))
    .sort(byStart);
  return { completed, upcoming };
};

// The vendor's state for the I-9 and Background check steps, as the row on
// those tabs shows it, or null when the vendor has nothing yet. `alert` is
// the tabs' red state (Suspended, Invitation expired, Rejected, a warning
// line): the panel marks it too, not as grey step-in-progress text (R6A-03).
const vendorStepText = (
  key: string,
  worker: WorkerListItem,
): { text: string; alert: boolean } | null => {
  const status =
    key === 'i9'
      ? i9StatusFor(worker)
      : key === 'background_check'
        ? bgStatusFor(worker)
        : null;
  if (!status || status.label === 'Not started') return null;
  return {
    text: status.warning ? `${status.label} • ${status.warning}` : status.label,
    alert: status.alert || Boolean(status.warning),
  };
};

const CHANNEL_LABEL: Record<string, string> = {
  push: 'Push',
  email: 'Email',
  sms: 'SMS',
};

const ChannelIcon = ({ channel }: { channel: string }) => {
  const sx = { fontSize: 16 };
  if (channel === 'email') return <EmailOutlinedIcon sx={sx} />;
  if (channel === 'sms') return <SmsOutlinedIcon sx={sx} />;
  return <NotificationsNoneIcon sx={sx} />;
};

// What the message log knows: a click, a delivery, otherwise the send status.
export const messageResult = (
  m: MessageTimelineItem,
): { label: string; tone: 'green' | 'neutral' } => {
  if (m.clicked_at) return { label: 'Opened', tone: 'green' };
  if (m.delivered_at) return { label: 'Delivered', tone: 'green' };
  const status = m.status.replace(/_/g, ' ');
  return {
    label: status.charAt(0).toUpperCase() + status.slice(1),
    tone: 'neutral',
  };
};

const Section = ({
  id,
  title,
  open,
  onToggle,
  adornment,
  description,
  children,
}: {
  id: string;
  title: string;
  open: boolean;
  onToggle: () => void;
  adornment?: ReactNode;
  // Read out with the heading: a hover tooltip on an icon inside the button
  // is unreachable by keyboard and screen readers (review D2-09).
  description?: string;
  children: ReactNode;
}) => (
  <section data-testid={`panelSection-${id}`}>
    {description && (
      <span id={`panel-${id}-desc`} hidden>
        {description}
      </span>
    )}
    <SectionHead
      type="button"
      aria-expanded={open}
      aria-controls={`panel-${id}`}
      aria-describedby={description ? `panel-${id}-desc` : undefined}
      onClick={onToggle}
    >
      <HeadLabel>
        {title}
        {adornment}
      </HeadLabel>
      {open ? (
        <KeyboardArrowUpIcon fontSize="small" />
      ) : (
        <KeyboardArrowDownIcon fontSize="small" />
      )}
    </SectionHead>
    {open && <div id={`panel-${id}`}>{children}</div>}
  </section>
);

type Props = {
  worker: WorkerListItem;
  activationShiftsTarget: number;
  onClose: () => void;
  // profileTab: a WorkerV2 profile tab key (PROFILE_TABS), e.g. 'messaging'.
  onOpenProfile: (id: number, profileTab?: string) => void;
  // Bumped by a row's "Rejected. View details": opens Onboarding progress at
  // the rejected step, also for the worker already open (review R5A-02).
  revealRejected?: number;
};

const WorkerPanel = ({
  worker,
  activationShiftsTarget,
  onClose,
  onOpenProfile,
  revealRejected = 0,
}: Props) => {
  const progress = worker.onboarding_progress;
  const activated = Boolean(progress?.completed);
  const [detail, setDetail] = useState<WorkerDetail | null>(null);
  const [overview, setOverview] = useState<WorkerLifecycleOverview | null>(
    null,
  );
  const [messages, setMessages] = useState<MessageTimelineItem[] | null>(null);
  // A failed call says so instead of reading as an empty result ("Not
  // contacted", "No claimed shift yet") or loading for ever (review D2-03).
  const [failed, setFailed] = useState({
    detail: false,
    overview: false,
    messages: false,
  });
  const [messagesReload, setMessagesReload] = useState(0);
  const fail = (key: keyof typeof failed) =>
    setFailed((f) => ({ ...f, [key]: true }));
  const [open, setOpen] = useState({
    onboarding: !activated,
    activation: activated,
    outreach: true,
  });

  // Swapping workers swaps the content and resets which section is open
  // (the one that applies opens by default, ticket WL-23).
  useEffect(() => {
    let cancelled = false;
    setDetail(null);
    setOverview(null);
    setFailed({ detail: false, overview: false, messages: false });
    setOpen({ onboarding: !activated, activation: activated, outreach: true });
    WorkersV2API.getWorker(worker.id)
      .then((r) => {
        if (cancelled) return;
        if (r.success) setDetail(r.data);
        else fail('detail');
      })
      .catch((err) => {
        console.error('[GravyWorkers] Error:', err);
        if (!cancelled) fail('detail');
      });
    lifecycleAPI
      .getWorkerOverview(worker.id)
      .then((o) => !cancelled && setOverview(o))
      .catch((err) => {
        console.error('[GravyWorkers] Error:', err);
        if (!cancelled) fail('overview');
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [worker.id]);

  useEffect(() => {
    let cancelled = false;
    setMessages(null);
    setFailed((f) => ({ ...f, messages: false }));
    // What went out, filtered by the server on sent_at: the timeline is
    // ordered by COALESCE(sent_at, scheduled_for), so a journey's future and
    // cancelled steps sort above every real send and a client-side filter of
    // the first 10 rows read "Not contacted" for a messaged worker (review
    // D2-01). A status filter does not work either: a send whose delivery
    // failed later is `failed` but went out (D2-01b), and a dispatch that
    // failed before sending is `failed` with no sent_at (R3B-02).
    lifecycleAPI
      .listWorkerMessages(worker.id, {
        kind: 'message',
        sent: true,
        per_page: 10,
      })
      .then(
        (r) =>
          !cancelled &&
          setMessages(
            r.data.filter(
              (m): m is MessageTimelineItem =>
                m.kind === 'message' && Boolean(m.sent_at),
            ),
          ),
      )
      .catch((err) => {
        console.error('[GravyWorkers] Error:', err);
        if (!cancelled) fail('messages');
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [worker.id, messagesReload]);

  // Keyboard focus follows the panel: Enter on a row's name opened it but
  // left focus in the table, so a keyboard or screen reader user did not
  // reach it. Not a focus trap: the panel is not modal. preventScroll, or
  // the sticky panel jumps the page to its heading.
  const headingRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    headingRef.current?.focus({ preventScroll: true });
  }, [worker.id]);

  // Closing gives focus back to this worker's name in the list; otherwise it
  // falls to <body> when the close button goes away. A worker opened from a
  // shared link may not be on this page, so there the search box takes it.
  const close = useCallback(() => {
    onClose();
    (
      document.querySelector<HTMLElement>(
        `[data-testid="workerName-${worker.id}"]`,
      ) ?? document.querySelector<HTMLElement>('[data-testid="searchInput"]')
    )?.focus({ preventScroll: true });
  }, [onClose, worker.id]);

  useEffect(() => {
    if (!revealRejected) return;
    setOpen((o) => ({ ...o, onboarding: true }));
    // After the section renders; jsdom has no scrollIntoView.
    requestAnimationFrame(() =>
      document
        .querySelector<HTMLElement>(
          '[data-testid="workerPanel"] [data-status="rejected"]',
        )
        ?.scrollIntoView?.({ block: 'nearest' }),
    );
  }, [revealRejected]);

  // Escape closes the panel, except while typing: the listener is on the
  // window, and Escape in the search box or a filter field clears or leaves
  // that field (review D2-11).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const typing =
        target?.isContentEditable ||
        ['INPUT', 'TEXTAREA', 'SELECT'].includes(target?.tagName ?? '');
      if (e.key === 'Escape' && !typing) close();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [close]);

  const name =
    worker.user?.full_name ??
    `${worker.user?.first_name ?? ''} ${worker.user?.last_name ?? ''}`.trim();
  const email = detail?.user?.email ?? worker.user?.email ?? '';
  const city = [worker.address?.city, worker.address?.state]
    .filter(Boolean)
    .join(', ');
  const journey = overview?.journeys.find((j) => !j.ended_at);
  const days = worker.current_status?.elapsed_days;
  const shifts = activationShifts(detail?.past_jobs ?? []);
  const completedCount = worker.shifts?.completed ?? shifts.completed.length;
  const lastShift = worker.shifts?.last_completed_at;

  const toggle = (key: keyof typeof open) =>
    setOpen((o) => ({ ...o, [key]: !o[key] }));

  return (
    <Aside
      role="complementary"
      aria-label={`Details for ${name}`}
      data-testid="workerPanel"
      data-worker-id={worker.id}
    >
      <div style={{ position: 'relative' }}>
        <CloseBtn
          type="button"
          aria-label="Close worker details"
          data-testid="workerPanelClose"
          onClick={close}
        >
          <CloseIcon />
        </CloseBtn>
      </div>
      <Scroll>
        <Avatar
          src={worker.avatar_url}
          firstName={worker.user?.first_name}
          lastName={worker.user?.last_name}
          a11yLabel={name}
        />
        <Name
          title={name}
          data-testid="panelName"
          ref={headingRef}
          tabIndex={-1}
        >
          {name}
        </Name>
        {email && <Line title={email}>{email}</Line>}
        {/* Joined, so a worker with no phone gets no leading " • " (review D2-13). */}
        <Line title={city}>
          {[formatPhone(worker.user?.phone_number), city]
            .filter(Boolean)
            .join(' • ')}
        </Line>
        <Faint>{formatActiveAgo(worker.last_active_at)}</Faint>

        <Tiles data-testid="panelTiles">
          {activated ? (
            <>
              <Tile tone="green">
                Stage<strong>Activation</strong>
              </Tile>
              <Tile tone="neutral">
                Last shift
                <strong>
                  {lastShift
                    ? `${formatDays(daysBetween(lastShift, new Date()))} ago`
                    : 'None yet'}
                </strong>
              </Tile>
            </>
          ) : (
            <>
              {/* The ticket draws "Stuck at" as a red tile for every worker
                  still onboarding (WL-23), not only after a rejection. */}
              <Tile tone="red" data-tone="red" data-testid="panelStuckAt">
                Stuck at
                <strong>
                  {progress?.current_step
                    ? `Step ${progress.current_step}`
                    : '—'}
                </strong>
              </Tile>
              <Tile tone="neutral">
                Days on step
                <strong>
                  {days === null || days === undefined ? '—' : formatDays(days)}
                </strong>
              </Tile>
            </>
          )}
          <Tile tone="neutral" data-testid="panelJourney">
            Journey
            <strong title={journey?.journey_name} data-wrap="true">
              {failed.overview
                ? '—'
                : overview
                  ? (journey?.journey_name ?? 'None')
                  : '…'}
            </strong>
          </Tile>
        </Tiles>

        {progress && (
          <Section
            id="onboarding"
            title="Onboarding progress"
            open={open.onboarding}
            onToggle={() => toggle('onboarding')}
            adornment={
              activated ? (
                <CheckCircleIcon
                  sx={{ fontSize: 18, color: '#44A735' }}
                  titleAccess="Onboarding complete"
                />
              ) : undefined
            }
          >
            <SummaryCard>
              <Faint style={{ fontWeight: 400, marginTop: 0 }}>
                Signed up {formatLongDate(worker.created_at)}
              </Faint>
              {activated
                ? 'All steps complete'
                : `Step ${progress.current_step} of ${progress.total_steps}: ${progress.current_step_label}`}
            </SummaryCard>
            <Timeline>
              {progress.steps.map((step) => {
                const done =
                  step.status === 'completed' ||
                  step.status === 'not_applicable';
                let sub = 'Not yet started';
                if (step.status === 'completed')
                  sub = step.completed_at
                    ? `${step.result} ${formatMonthDayTime(step.completed_at)}`
                    : (step.result ?? 'Completed');
                else if (step.status === 'not_applicable')
                  sub = step.result ?? 'Not required';
                else if (step.status === 'rejected')
                  sub = progress.rejection_reason
                    ? `Rejected: ${progress.rejection_reason}`
                    : 'Rejected';
                else if (step.status === 'current') sub = 'In progress';
                // A step that has not started on our side can be under way
                // at the vendor: the I-9 (handbook first) and Checkr run
                // alongside. Say what the I-9 and Background check tabs say
                // rather than "Not yet started" (review R5A-07).
                const vendor =
                  step.status === 'pending' || step.status === 'current'
                    ? vendorStepText(step.key, worker)
                    : null;
                if (vendor) sub = vendor.text;
                // graphy settles step 4 on an E-Verify tentative
                // non-confirmation (Product Q5) and says "Info mismatch";
                // a green check beside it read as verified. The step stays
                // done for the counts; only its marker turns red (BH-37).
                const mismatch =
                  step.key === 'i9' &&
                  step.status === 'completed' &&
                  i9Mismatch(worker);
                if (mismatch)
                  sub =
                    `Info mismatch ${formatMonthDayTime(step.completed_at)}`.trim();
                const alert =
                  step.status === 'rejected' ||
                  Boolean(vendor?.alert) ||
                  mismatch;
                return (
                  <Item
                    key={step.key}
                    data-testid={`panelStep-${step.key}`}
                    data-status={step.status}
                    data-alert={alert || undefined}
                  >
                    <Dot done={done} alert={alert} />
                    <ItemTitle>
                      Step {step.number} of {progress.total_steps}: {step.label}
                    </ItemTitle>
                    {/* BH-73: a rejection note is free text. A very long
                        word ran past the panel edge, which hides overflow,
                        so its end could not be read anywhere. */}
                    <ItemSub alert={alert} style={{ overflowWrap: 'anywhere' }}>
                      {sub}
                    </ItemSub>
                  </Item>
                );
              })}
            </Timeline>
          </Section>
        )}

        <Section
          id="activation"
          title="Activation progress"
          open={open.activation}
          onToggle={() => toggle('activation')}
        >
          <SummaryCard>
            {Math.min(completedCount, activationShiftsTarget)} of{' '}
            {activationShiftsTarget} shifts completed
          </SummaryCard>
          <Timeline>
            <Item data-testid="panelShift-onboarding">
              <Dot done={activated} />
              <ItemTitle>Onboarding complete</ItemTitle>
              <ItemSub>
                {activated
                  ? `All ${progress?.total_steps ?? 6} steps completed ${formatMonthDay(progress?.onboarding_completed_at)}`.trim()
                  : 'Not yet'}
              </ItemSub>
            </Item>
            {Array.from({ length: activationShiftsTarget }, (_, i) => {
              const doneShift: ShiftEntry | undefined = shifts.completed[i];
              const upcoming: ShiftEntry | undefined = doneShift
                ? undefined
                : shifts.upcoming[i - shifts.completed.length];
              let sub = 'No claimed shift yet';
              if (doneShift)
                sub = `Completed ${formatMonthDay(doneShift.at)} • ${doneShift.role}`;
              else if (upcoming)
                sub = `Scheduled ${formatMonthDay(upcoming.at)} • ${upcoming.role}`;
              return (
                // eslint-disable-next-line react/no-array-index-key
                <Item key={i} data-testid={`panelShift-${i + 1}`}>
                  <Dot done={Boolean(doneShift)} />
                  <ItemTitle>
                    {i === 0 ? 'First shift' : `Shift ${i + 1}`}
                  </ItemTitle>
                  <ItemSub>
                    {failed.detail
                      ? 'Could not load shifts'
                      : detail
                        ? sub
                        : '…'}
                  </ItemSub>
                </Item>
              );
            })}
          </Timeline>
        </Section>

        <Section
          id="outreach"
          title="Outreach"
          open={open.outreach}
          onToggle={() => toggle('outreach')}
          description={OUTREACH_TOOLTIP}
          adornment={
            <CardTooltip title={OUTREACH_TOOLTIP}>
              {/* A click on the icon is for its tooltip, not the toggle. */}
              <InfoOutlinedIcon
                sx={{ fontSize: 16 }}
                data-testid="outreachInfo"
                onClick={(e) => e.stopPropagation()}
              />
            </CardTooltip>
          }
        >
          <MessageHead>
            <Muted>Sent</Muted>
            <ViewAll
              type="button"
              data-testid="outreachViewAll"
              // The full message history is the profile's Messaging tab.
              onClick={() => onOpenProfile(worker.id, 'messaging')}
            >
              View all
            </ViewAll>
          </MessageHead>
          {failed.messages && (
            <Muted data-testid="outreachError">
              Could not load messages.{' '}
              <ViewAll
                type="button"
                onClick={() => setMessagesReload((n) => n + 1)}
              >
                Retry
              </ViewAll>
            </Muted>
          )}
          {messages === null && !failed.messages && (
            <Muted>Loading messages…</Muted>
          )}
          {messages?.length === 0 && (
            <Muted data-testid="outreachEmpty">Not contacted</Muted>
          )}
          {messages && messages.length > 0 && (
            <ul
              style={{ listStyle: 'none', margin: 0, padding: 0 }}
              data-testid="outreachList"
            >
              {messages.slice(0, OUTREACH_PREVIEW).map((m) => {
                const channel = m.channel_used ?? m.channel_planned;
                const result = messageResult(m);
                return (
                  <Message key={m.id} data-testid={`outreach-${m.id}`}>
                    <MessageHead>
                      <Channel>
                        <ChannelIcon channel={channel} />
                        {CHANNEL_LABEL[channel] ?? channel}
                      </Channel>
                      <Chip tone={result.tone}>{result.label}</Chip>
                    </MessageHead>
                    <Faint>
                      Sent {formatMonthDay(m.sent_at)} • Day{' '}
                      {daysBetween(worker.created_at, m.sent_at!)}
                    </Faint>
                    {m.body_preview && <Quote>“{m.body_preview}”</Quote>}
                  </Message>
                );
              })}
            </ul>
          )}
        </Section>
      </Scroll>
      <Footer>
        {/* What these do is ticket open question Q10. */}
        <CardTooltip title={NOT_AVAILABLE_REASON}>
          <span>
            <FooterBtn
              type="button"
              primary
              disabled
              data-testid="panelTakeOver"
            >
              Take over
            </FooterBtn>
          </span>
        </CardTooltip>
        <CardTooltip title={NOT_AVAILABLE_REASON}>
          <span>
            <FooterBtn type="button" disabled data-testid="panelViewJob">
              View job
            </FooterBtn>
          </span>
        </CardTooltip>
      </Footer>
    </Aside>
  );
};

export default WorkerPanel;
